import type { ReadonlyURLSearchParams } from 'next/navigation';
import type { Hotel } from './types/hotels.types';

export const PUBLIC_RESULTS_QUERY_KEYS = ['q', 'checkIn', 'checkOut', 'guests', 'location', 'radius', 'stars', 'types', 'amenities', 'rating', 'sort', 'view'] as const;
export const PUBLIC_BOOKING_QUERY_KEYS = ['q', 'checkIn', 'checkOut', 'guests', 'room', 'location', 'radius', 'stars', 'types', 'amenities', 'rating', 'sort', 'view'] as const;

function normalizeSlug(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function getHotelSlugCandidates(hotel: Hotel) {
  const fullNameSlug = normalizeSlug(hotel.name);
  const trimmedNameSlug = normalizeSlug(hotel.name.replace(/^the\s+/i, ''));

  return new Set(
    [hotel.id, fullNameSlug, trimmedNameSlug]
      .filter(Boolean)
      .map((value) => normalizeSlug(value)),
  );
}

export function findHotelBySlug(slug: string, hotels: Hotel[]) {
  const normalized = normalizeSlug(slug);
  return hotels.find((hotel) => getHotelSlugCandidates(hotel).has(normalized));
}

export function createQueryString(
  params: Record<string, string | null | undefined>,
) {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    const trimmed = value?.trim();
    if (trimmed) {
      query.set(key, trimmed);
    }
  });

  return query.toString();
}

export function pickSearchParams(
  searchParams: ReadonlyURLSearchParams,
  keys: readonly string[],
) {
  return createQueryString(
    keys.reduce<Record<string, string | null>>((acc, key) => {
      acc[key] = searchParams.get(key);
      return acc;
    }, {}),
  );
}

export function withQuery(path: string, query: string) {
  return query ? `${path}?${query}` : path;
}
